import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { brandsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Tag,
    Plus,
    Pencil,
    Trash2,
    Building2,
    Loader2,
    Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PortalLogoUpload from '../components/branding/PortalLogoUpload';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';

const AdminBrands = () => {
    const { t } = useTranslation();
    const { isSuperAdmin, isPartner } = useAuth();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        try {
            const response = await brandsAPI.list();
            setBrands(response.data);
        } catch (error) {
            toast.error('Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedBrand(null);
        setFormData({ name: '', description: '' });
        setShowModal(true);
    };

    const handleEdit = (brand) => {
        setSelectedBrand(brand);
        setFormData({ name: brand.name, description: brand.description || '' });
        setShowModal(true);
    };

    const handleDelete = (brand) => {
        setSelectedBrand(brand);
        setShowDeleteDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (selectedBrand) {
                await brandsAPI.update(selectedBrand.id, formData);
                toast.success('Brand updated successfully');
            } else {
                await brandsAPI.create(formData);
                toast.success('Brand created successfully');
            }
            setShowModal(false);
            fetchBrands();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save brand');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            await brandsAPI.delete(selectedBrand.id);
            toast.success('Brand deleted successfully');
            setShowDeleteDialog(false);
            fetchBrands();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete brand');
        }
    };

    const filteredBrands = brands.filter(brand =>
        brand.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isSuperAdmin() && !isPartner()) {
        return (
            <div className="p-8 text-center">
                <p className="text-muted-foreground">Access denied. Super Admin only.</p>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground dark:text-white">
                        Brand Management
                    </h1>
                    <p className="text-muted-foreground dark:text-muted-foreground mt-1">
                        Create and manage brands to group organizations
                    </p>
                </div>
                {!isPartner() && <Button onClick={handleCreate} data-testid="create-brand-btn" data-insight-id="admin-brands.workflow.create" data-insight-label="Create a brand">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Brand
                </Button>}
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Search brands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="brand-search"
                />
            </div>

            {/* Brands Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            ) : filteredBrands.length === 0 ? (
                <div className="text-center py-12 bg-background dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <Tag className="w-12 h-12 mx-auto text-muted-foreground dark:text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground dark:text-white mb-2">
                        {searchTerm ? 'No brands found' : 'No brands yet'}
                    </h3>
                    <p className="text-muted-foreground dark:text-muted-foreground mb-4">
                        {searchTerm ? 'Try a different search term' : 'Create your first brand to get started'}
                    </p>
                    {!searchTerm && (
                        <Button onClick={handleCreate}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Brand
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBrands.map((brand, idx) => (
                        <div
                            key={brand.id}
                            className="bg-background dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg transition-shadow"
                            data-testid={`brand-card-${brand.id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                        <Tag className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground dark:text-white">
                                            {brand.name}
                                        </h3>
                                        <div className="flex items-center gap-1 text-sm text-muted-foreground dark:text-muted-foreground">
                                            <Building2 className="w-3.5 h-3.5" />
                                            <span>{brand.organization_count || 0} organizations</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {brand.description && (
                                <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-4 line-clamp-2">
                                    {brand.description}
                                </p>
                            )}

                            {!isPartner() && (
                            <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEdit(brand)}
                                    data-testid={`edit-brand-${brand.id}`}
                                >
                                    <Pencil className="w-3.5 h-3.5 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(brand)}
                                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                                    data-testid={`delete-brand-${brand.id}`}
                                    data-insight-id={idx === 0 ? "admin-brands.workflow.delete" : undefined}
                                    data-insight-label={idx === 0 ? "Delete brand" : undefined}
                                >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    Delete
                                </Button>
                            </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-hidden grid grid-rows-[auto_1fr_auto] p-0">
                    <DialogHeader className="px-6 pt-6 pb-2">
                        <DialogTitle>
                            {selectedBrand ? 'Edit Brand' : 'Create Brand'}
                        </DialogTitle>
                    </DialogHeader>
                    <form
                        onSubmit={handleSubmit}
                        className="contents"
                    >
                        <div className="overflow-y-auto px-6 pb-2 space-y-4">
                            <div>
                                <Label htmlFor="name">Brand Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Pizza Hut"
                                    required
                                    data-testid="brand-name-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="description">Description</Label>
                                <Input
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder={t('adminBrands.optionalDescription')}
                                    data-testid="brand-description-input"
                                />
                            </div>

                            {/* v4.30 — Brand-level portal identity. Inherited by every
                              * organization under this brand. Org-level overrides
                              * still win at the org edit screen. */}
                            <PortalLogoUpload
                                endpoint={selectedBrand
                                    ? `/brands/${selectedBrand.id}/portal-logo`
                                    : ''}
                                currentLight={selectedBrand?.logo_url || null}
                                currentDark={selectedBrand?.logo_url_dark || null}
                                disabled={!selectedBrand}
                                kind="brand"
                                previewOrgId={selectedBrand?.id || null}
                                previewOrgName={selectedBrand?.name || formData.name || null}
                                previewBrandName={selectedBrand?.name || null}
                                onUploaded={({ variant, logo_url }) => {
                                    setSelectedBrand((prev) => prev ? {
                                        ...prev,
                                        [variant === 'dark' ? 'logo_url_dark' : 'logo_url']: logo_url,
                                    } : prev);
                                }}
                            />
                        </div>
                        <DialogFooter className="px-6 pb-6 pt-3 border-t border-slate-200 dark:border-slate-700 bg-background flex-wrap gap-2">
                            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="save-brand-btn">
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    selectedBrand ? 'Update Brand' : 'Create Brand'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Brand?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{selectedBrand?.name}"? 
                            This action cannot be undone. You must first remove all organizations from this brand.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminBrands;
